import Cookies from 'js-cookie';

import { TranslationHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { useDidMount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { Language } from '@bcp-channels-sharedcomponents-reactjs/core/dist/types';

import { AmaHelper, AppHelper, LoggerHelper } from './helpers';
import { useURLSearchParams } from './hooks';
import { useFetchAccountUser } from './services';
import {
	CookieName,
	CookieResponseTypeCode,
	useBaseStore,
	useCookiesStore,
	useLoginStore,
	useResetSessionStore,
	useSaveServiceResponse,
	useSearchParamsStore,
} from './store';

export const useAppHelper = () => {
	const { searchParams } = useURLSearchParams();
	const flow = useLoginStore((state) => state.flow);
	const searchParamsStore = useSearchParamsStore();
	const setLoginState = useLoginStore((state) => state.setState);
	const setCookiesState = useCookiesStore((state) => state.setState);
	const saveServiceResponse = useSaveServiceResponse();
	const resetSessionStore = useResetSessionStore();
	const goto = useBaseStore((state) => state.goto);

	const { fetch: fetchAccountUser } = useFetchAccountUser();

	useDidMount(() => {
		handleAppEntry();
	});

	const handleAppEntry = async () => {
		TranslationHelper.changeLanguage(searchParams.language || searchParamsStore.language || Language.pt);

		const { isBeRedirect, responseTypeCookie, responseCookie } = getBeRedirect();
		const isAmaRedirect = AmaHelper.isAmaRedirect();

		LoggerHelper.log('App', 'handleAppEntry:', {
			isBeRedirect,
			responseTypeCookie,
			responseCookie,
			isAmaRedirect,
		});

		if (isBeRedirect) {
			/* cookies have been sent from BE */
			handleBeRedirect(responseTypeCookie, responseCookie);
		} else if (isAmaRedirect) {
			goto('login');
		} else {
			handleAppStart();
		}
	};

	const handleAppStart = async () => {
		LoggerHelper.log('App', 'handleAppEntry > handleAppStart:', { searchParams });

		try {
			/* validate query params */
			const { epoch, fallbackUri } = searchParams;
			const clientId = searchParams.clientId || searchParamsStore.clientId;
			const redirectUri = searchParams.redirectUri || searchParamsStore.redirectUri;

			if (!clientId || !redirectUri) {
				throw Error('Missing "client_id" and "redirect_uri" query parameters');
			}

			/* clear the storage when it's not a redirect from be (just in case) */
			resetSessionStore();

			/* store search params to be used through BE requests */
			searchParamsStore.setState(searchParams.clientId ? searchParams : searchParamsStore);

			/* validate epoch param */
			const validEpoch = AppHelper.isValidEpochParam(clientId, epoch);
			if (!validEpoch) {
				const redirectOrigin = AppHelper.getOrigin(redirectUri);
				window.open(fallbackUri || redirectOrigin, '_self');
				return;
			}

			/* both k and sf params have been passed,
				fetch accountUser service (without breaking layout)
				to verify if IDP should "login" automatically */
			if (searchParams.k && searchParams.sf) {
				try {
					const res = await fetchAccountUser({
						k: searchParams.k,
						sf: searchParams.sf,
					});

					const { userAlias } = res.data || {};
					if (userAlias) {
						setLoginState({ form: { alias: userAlias } });
					}
				} catch {}
			}

			/* valid session, go to login screen */
			goto('login');
		} catch (error) {
			const catchError = AppHelper.convertCatchError(error);
			LoggerHelper.error('App', 'handleAppEntry > handleAppStart [catch]:', catchError, {
				overlay: { show: true },
			});
			goto('error');
		}
	};

	const handleBeRedirect = async (responseTypeCookie: CookieResponseTypeCode, responseCookie: string) => {
		LoggerHelper.log('App', 'handleAppEntry > handleBeRedirect:', {
			responseType: responseTypeCookie,
			response: responseCookie,
		});

		try {
			const output = await handleCookies(responseTypeCookie, responseCookie);

			if (!output) {
				throw Error('Missing output in handleCookies function.');
			}

			const { responseType, response } = output;

			AppHelper.isBeRedirect = true;

			/* save service response -
			if the flow has been set to 'updateCredentials', the BE response should be different */
			const responseKey = flow === 'updateCredentials' ? 'accountUserCredentials' : 'accountChallenge';
			saveServiceResponse({
				[responseKey]: response,
			});

			/* when cookie response is 'updateCredentials' or the flow was already 'updateCredentials',
			should set the correct flow and navigate to correct screen */
			if (responseType === 'updateCredentials' || flow === 'updateCredentials') {
				setLoginState({ flow: 'updateCredentials' });
				goto('updateCredentials');
			} else {
				goto('login');
			}
		} catch (error) {
			const catchError = AppHelper.convertCatchError(error);
			LoggerHelper.error('App', 'handleAppEntry > handleBeRedirect [catch]:', catchError, {
				overlay: { show: true },
			});
			goto('error');
		}
	};

	const handleCookies = async (responseType: CookieResponseTypeCode, response: string) => {
		try {
			/* store the cookies response */
			setCookiesState({
				responseType,
				response,
			});

			/* clear the cookies */
			/* (only works for localhost, since it's BE that sets the cookie, with a defined domain and secure) */
			/* (implemented "account/deleteCookie" service in login.helper and updateCredentials.helper) */
			Cookies.remove(CookieName.ResponseType, { path: '/' });
			Cookies.remove(CookieName.Response, { path: '/' });

			/* convert the cookie response to a valid object */
			/* convert to object */
			const responseObject = (response && JSON.parse(response)) || {};
			/* convert from PascalCase to camelCase */
			const responseCamelCase = AppHelper.camelizeKeys(responseObject);

			return {
				responseType,
				response: responseCamelCase,
			};
		} catch (err) {
			const catchError = AppHelper.convertCatchError(err);
			LoggerHelper.log('App', 'App.helper > handleCookies [catch] :', catchError, { overlay: { show: true } });
			Promise.reject();
		}
	};

	const getBeRedirect = () => {
		/* cookies sent from BE */
		const responseTypeCookie = (Cookies.get(CookieName.ResponseType) || '') as CookieResponseTypeCode;
		const responseCookie = Cookies.get(CookieName.Response) || '';
		const isBeRedirect = !!responseTypeCookie;

		return { responseTypeCookie, responseCookie, isBeRedirect };
	};
};
